import { useEffect, useMemo, useRef, useState } from 'react';
import { SupabaseClient, PostgrestError } from '@supabase/supabase-js';
import { Database } from '@/types_db';

type TableName = keyof Database['public']['Tables'];
type TableRow<T extends TableName> = Database['public']['Tables'][T]['Row'];

interface UsePollingQueryOptions<T extends TableName> {
  supabase: SupabaseClient<Database>;
  table: T;
  select?: string;
  filter?: [column: string, operator: string, value: any];
  defaultValue?: TableRow<T>[];
  intervalMs?: number;
  stopWhen?: (rows: TableRow<T>[]) => boolean;
  enabled?: boolean;
}

interface UsePollingQueryReturn<Row> {
  data: Row[];
  loading: boolean;
  error: PostgrestError | null;
}

const DEFAULT_INTERVAL_MS = 3000;

function usePollingQuery<T extends TableName>({
  supabase,
  table,
  select = '*',
  filter,
  defaultValue = [],
  intervalMs = DEFAULT_INTERVAL_MS,
  stopWhen,
  enabled = true,
}: UsePollingQueryOptions<T>): UsePollingQueryReturn<TableRow<T>> {
  const [data, setData] = useState<TableRow<T>[]>(defaultValue);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<PostgrestError | null>(null);

  const memoizedFilter = useMemo(() => filter, [JSON.stringify(filter)]);
  const stopWhenRef = useRef(stopWhen);
  stopWhenRef.current = stopWhen;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let isMounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;
    let fetchInFlight = false;
    let lastDataSignature = '';

    const runFetch = async () => {
      if (fetchInFlight) return;
      fetchInFlight = true;
      try {
        let query = supabase.from(table).select(select);
        if (memoizedFilter) {
          const [column, operator, value] = memoizedFilter;
          if (operator === 'in') {
            query = query.in(column, value);
          } else {
            query = query.filter(column, operator as any, value);
          }
        }
        const { data: rows, error: fetchError } = await query;
        if (!isMounted) return;
        if (fetchError) {
          setError(fetchError);
          return;
        }
        const typedRows = (rows ?? []) as unknown as TableRow<T>[];
        const signature = JSON.stringify(typedRows);
        if (signature !== lastDataSignature) {
          lastDataSignature = signature;
          setData(typedRows);
        }
        setError(null);
        if (stopWhenRef.current?.(typedRows)) stopped = true;
      } finally {
        fetchInFlight = false;
      }
    };

    const scheduleNext = () => {
      if (!isMounted || stopped) return;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(tick, intervalMs);
    };

    const tick = async () => {
      if (!isMounted || stopped) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        scheduleNext();
        return;
      }
      await runFetch();
      scheduleNext();
    };


    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!isMounted || stopped) return;
      if (timeoutId) clearTimeout(timeoutId);
      runFetch().then(scheduleNext);
    };

    setLoading(true);
    runFetch().finally(() => {
      if (isMounted) setLoading(false);
      scheduleNext();
    });

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    };
  }, [supabase, table, select, memoizedFilter, intervalMs, enabled]);

  return { data, loading, error };
}

export default usePollingQuery;
